import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'

import { Typography, Box } from '@material-ui/core'
import { faBookmark, faIdCard, faSparkles } from '@fortawesome/pro-regular-svg-icons'
import {
  faBookmark as faSolidBookmark,
  faIdCard as faSolidIdCard,
  faSparkles as faSolidSparkles,
} from '@fortawesome/pro-solid-svg-icons'

import {
  getDefaultFollowersSegment,
  getDefaultMyMatchesSegment,
  getDefaultStudentIdSegment,
  getIsUsingManualStudentSelection,
  getIsUsingMyMatches,
} from 'components/SegmentSelector/segment-service'
import { getUserType } from 'components/SegmentSelector/UserTypeSelector'
import useCollegeFeatures from 'common/use-college-features'
import features from 'common/college-features'
import { ONE_TIME_TRIGGER } from 'components/SegmentSelector/outreach-message-trigger-constants'
import { isStudent } from 'components/SegmentSelector/UserTypeSelector'
import IconRadioGroup from 'components/IconRadioGroup'

export const audiences = {
  followers: 'Followers',
  manual: 'List of Scoir Student IDs',
  matches: 'My Matches',
}

const getOptions = (selectedAudience, showManual, showMatches, isStudentMsg) => {
  const options = []

  options.push({
    title: 'Followers',
    subtitle: isStudentMsg
      ? 'Students who are Following, Applying, or have Applied to your school'
      : 'Parents/guardians of students who are Following, Applying, or have Applied to your school',
    faIcon: selectedAudience === audiences.followers ? faSolidBookmark : faBookmark,
    value: audiences.followers,
    selected: selectedAudience === audiences.followers,
  })

  if (showMatches) {
    options.push({
      title: 'My Matches',
      subtitle: `Matched prospects who aren't following you yet`,
      faIcon: selectedAudience === audiences.matches ? faSolidSparkles : faSparkles,
      value: audiences.matches,
      selected: selectedAudience === audiences.matches,
    })
  }

  if (showManual) {
    options.push({
      title: 'List of Student IDs',
      subtitle: 'Scoir Student IDs from your CRM',
      faIcon: selectedAudience === audiences.manual ? faSolidIdCard : faIdCard,
      value: audiences.manual,
      selected: selectedAudience === audiences.manual,
    })
  }

  return options
}

const AudienceTypeSelector = ({ trigger, children, segment, onChange }) => {
  const isManualStudentEntryEnabled = useCollegeFeatures(features.SUPPORTS_USER_UPLOADED_SEGMENTS)
  const isUsingManualStudentSelection = getIsUsingManualStudentSelection(segment)
  const isUsingMyMatches = getIsUsingMyMatches(segment)
  const [selectedAudience, setSelectedAudience] = useState(
    isUsingManualStudentSelection ? audiences.manual : isUsingMyMatches ? audiences.matches : audiences.followers,
  )

  const isOneTimeMsg = trigger === ONE_TIME_TRIGGER
  const isStudentMsg = isStudent(segment)
  const showManual = isManualStudentEntryEnabled && isOneTimeMsg && isStudentMsg
  const showMatches = isStudentMsg && isOneTimeMsg
  const userType = getUserType(segment)

  const lastUserType = useRef(userType)
  useEffect(() => {
    if (lastUserType.current !== userType) {
      setSelectedAudience(audiences.followers)
      lastUserType.current = userType
    }
  }, [userType])

  const selectAudience = useCallback(
    (value) => {
      setSelectedAudience(value)
      if (value === audiences.manual) {
        onChange(getDefaultStudentIdSegment())
      } else if (value === audiences.matches) {
        const defaultMyMatchesSegment = getDefaultMyMatchesSegment(trigger, userType)
        onChange(defaultMyMatchesSegment)
      } else {
        const defaultFollowersSegment = getDefaultFollowersSegment(trigger, userType)
        onChange(defaultFollowersSegment)
      }
    },
    [onChange, trigger, setSelectedAudience, userType],
  )

  if (!isOneTimeMsg) {
    return children
  }

  return (
    <React.Fragment>
      <Box pb={2}>
        <Typography
          variant="subtitle2"
          color="textPrimary"
        >
          Select segment
        </Typography>
      </Box>
      <IconRadioGroup
        options={getOptions(selectedAudience, showManual, showMatches, isStudentMsg)}
        onSelect={(value) => {
          selectAudience(value)
        }}
      />
      {children}
    </React.Fragment>
  )
}

AudienceTypeSelector.propTypes = {
  trigger: PropTypes.string,
  children: PropTypes.any,
  segment: PropTypes.object,
  onChange: PropTypes.func.isRequired,
}

export default AudienceTypeSelector
